import { useMutation } from '@apollo/client';
import { usePrevious } from '@uidotdev/usehooks';
import { first, last } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import { useAgendaAvailableItems } from '../../../../data/data/agenda/use-agenda-available-items';
import { useAgendaFirstAvailableItem } from '../../../../data/data/agenda/use-agenda-first-available-item';
import { useBulkSlotItems } from '../../../../data/data/bulk-messages/use-bulk-slot-items';
import { useAvailableAppointmentHonorificBlockMinuteValues } from '../../../../data/data/honorific-block-times/use-available-appointment-honorific-block-minute-values';
import {
  AgendaAvailableSlotItemDataFragment,
  BaseBulkItemDataFragment,
  BaseHealthProfessionalItemFragment,
  BulkSlotItemDataFragment,
  ConsultType,
  DeleteAgendaAppointmentItemDocument,
  GetBulkItemsDocument,
  GetBulkSlotsDocument,
  Honorific,
  MutateAgendaAppointmentItemDocument,
  TemplateFrequency,
} from '../../../../data/types/api/graphql';
import { DateTimeFormat, formatDate } from '../../../../data/utils/date.utils';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { InputCheckboxController } from '../../../../elements/form/controllers/input-checkbox';
import { InputFieldController } from '../../../../elements/form/controllers/input-field';
import { InputSelectController } from '../../../../elements/form/controllers/input-select';
import { FormColumns, FormRow } from '../../../../elements/form/form';
import { BaseInputIcon } from '../../../../elements/form/input/base-input';
import { InputField } from '../../../../elements/form/input/input-field';
import { InputFieldPager } from '../../../../elements/form/input-field-pager';
import { CustomFormattedDate } from '../../../../elements/intl/custom-formatted-date';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Message, MessageType } from '../../../../elements/message/message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';
import { AgendaAvailableBlockList } from '../../../agenda/list/agenda-available-block-list';
import { BulkMessagesSlotList } from '../../list/bulk-messages-slot-list/bulk-messages-slot-list';
import { useBulkMessageSlotsForm } from './form/use-bulk-message-slots-form';

type BulkMessageSlotsModalProps = {
  bulkItem?: BaseBulkItemDataFragment | null;
  onSaved?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
};

enum BulkMessageSlotModalAction {
  LIST = 'LIST',
  EDIT = 'EDIT',
}

function BulkMessageSlotsModal({
  bulkItem,
  onCancel,
  onClose,
}: BulkMessageSlotsModalProps) {
  // get item if id
  const intl = useIntl();

  const { form, formIsValid } = useBulkMessageSlotsForm();
  const { showAlertMessage, healthProviderId } = useAppContext();

  const { data: bulkSlotItems, loading: bulkSlotItemsLoading } =
    useBulkSlotItems({
      id: bulkItem?.id,
    });

  const [cancelPromptOpen, setCancelPromptOpen] = useState<boolean>(false);

  const [action, setAction] = useState<BulkMessageSlotModalAction>(
    BulkMessageSlotModalAction.LIST
  );

  const onHandleCancel = useCallback(
    (confirmed: boolean = false) => {
      if (!confirmed && form.formState.isDirty) {
        setCancelPromptOpen(true);
      } else {
        onCancel?.();

        onClose?.();
      }
    },
    [form.formState.isDirty, onCancel, onClose]
  );

  const healthProfessionalId = form.getValues('healthProfessionalId');
  const consultType = ConsultType.Physical;
  const honorific = form.watch('honorific');
  const irregular = form.watch('irregular');
  const minutes = form.watch('minutes');
  const previousHonorific = usePrevious(honorific);

  const [
    mutateAgendaAppointmentItem,
    { loading: mutateAgendaAppointmentItemLoading },
  ] = useMutation(MutateAgendaAppointmentItemDocument, {
    refetchQueries: [GetBulkSlotsDocument, GetBulkItemsDocument],
  });

  const [selectedSlots, setSelectedSlots] = useState<
    AgendaAvailableSlotItemDataFragment[]
  >([]);
  const [date, setDate] = useState<string>(formatDate(new Date()));
  const onHandleDate = useCallback((value: string) => {
    setDate(value);
  }, []);
  const onHandleDatePager = useCallback(
    (type: 'prev' | 'next') => {
      const currentDate = new Date(date);

      if (type === 'prev') {
        currentDate.setDate(currentDate.getDate() - 1);
      }

      if (type === 'next') {
        currentDate.setDate(currentDate.getDate() + 1);
      }

      setDate(formatDate(currentDate));
    },
    [date]
  );

  const { prevDate, nextDate } = useMemo(
    () => ({
      prevDate:
        date &&
        new Date().setHours(0, 0, 0, 0) < new Date(date).setHours(0, 0, 0, 0),
      nextDate: date,
    }),
    [date]
  );

  const {
    data: availableHonorificData,
    loading: availableHonorificDataLoading,
  } = useAvailableAppointmentHonorificBlockMinuteValues({
    healthProviderId,
    internalAgenda: true,
    healthProfessionalId: null,
    consultTypes: [consultType],
  });

  const honorifics = useMemo(
    () => availableHonorificData.map(x => ({ key: x.key, title: x.title })),
    [availableHonorificData]
  );

  const canSkipAgendaAvailableItems = () => {
    if (action !== BulkMessageSlotModalAction.EDIT) return true;
    if (!honorific) return true;
    if (!minutes) return true;
    return false;
  };

  const { data: blocks, loading: blocksLoading } = useAgendaAvailableItems(
    {
      healthProviderId,
      startDate: date,
      irregular: irregular === true, // todo?
      endDate: date,
      consultType,
      honorifics: honorific ? [honorific] : null,
      frequency: TemplateFrequency.Once,
      healthProfessionalId: null,
      minuteLength: minutes ?? null,
      currentAppointmentId: null,
    },
    canSkipAgendaAvailableItems()
  );

  const canSkipFirstAvailableItem = () => {
    if (action !== BulkMessageSlotModalAction.EDIT) return true;
    if (!honorific) return true;
    if (previousHonorific && honorific && honorific === previousHonorific)
      return true;
    if (blocksLoading === true && blocks.length > 0) return true;
    if (!minutes) return true;
    return false;
  };

  const { data: firstAvailableDates } = useAgendaFirstAvailableItem(
    {
      healthProviderId,
      consultType,
      honorific: honorific ? [honorific] : null,
      healthProfessionalId: null,
      minutes: minutes ?? null,
    },
    canSkipFirstAvailableItem()
  );

  const { next } = useMemo(() => {
    if (
      !blocksLoading &&
      blocks.length === 0 &&
      firstAvailableDates &&
      firstAvailableDates?.length > 0 &&
      date
    ) {
      const now = formatDate(new Date(date));
      return {
        previous: last(firstAvailableDates?.filter(d => d < now)) ?? null,
        next: first(firstAvailableDates?.filter(d => d > now)) ?? null,
      };
    }
    return { previous: null, next: null };
  }, [blocks.length, blocksLoading, date, firstAvailableDates]);

  const { blockTimes } = useMemo(() => {
    if (availableHonorificDataLoading) {
      return {
        blockTimes: [],
        defaultBlockTime: 0,
      };
    }

    const item = availableHonorificData.find(x => x.key === honorific);

    const times =
      item?.values?.map(value => ({
        key: Number(value.key),
        title: value.title,
        irregular: value.irregular,
      })) ?? [];

    let calculatedDefaultTime = item?.defaultValue ?? null;

    // calculate closest
    if (date && times.length) {
      const appointmentLength = Math.round(new Date(date).getTime() / 60000);
      if (appointmentLength > 0) {
        calculatedDefaultTime = times.reduce((closest, curr) =>
          Math.abs(curr.key - appointmentLength) <
          Math.abs(closest.key - appointmentLength)
            ? curr
            : closest
        ).key;
      }
    }

    return {
      blockTimes: times,
      defaultBlockTime: calculatedDefaultTime,
    };
  }, [availableHonorificData, availableHonorificDataLoading, date, honorific]);

  const filteredBlockTimes = useMemo(
    () =>
      irregular ? blockTimes : blockTimes.filter(x => x.irregular === false),
    [blockTimes, irregular]
  );

  const { range } = useMemo(() => {
    const all = selectedSlots.map(item => ({
      startDateTime: formatDate(item.startTime, DateTimeFormat),
      endDateTime: formatDate(item.endTime, DateTimeFormat),
    }));

    const lastItem = all[all.length - 1];
    const firstItem = all[0];

    return { range: all, firstInRange: firstItem, lastInRange: lastItem };
  }, [selectedSlots]);

  const onHandleSlots = useCallback(
    (
      healthProfessional: BaseHealthProfessionalItemFragment,
      slots: AgendaAvailableSlotItemDataFragment[]
    ) => {
      if (healthProfessionalId !== healthProfessional.id) {
        form.setValue('healthProfessionalId', healthProfessional.id, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
      setSelectedSlots(slots);
    },

    [form, healthProfessionalId]
  );

  const resetSlots = useCallback(() => {
    form.reset({
      healthProfessionalId: '',
      honorific: null,
      minutes: 0,
      irregular: false,
      slots: 1,
    });
    setAction(BulkMessageSlotModalAction.LIST);
    setSelectedSlots([]);
  }, [form]);

  const [removeBulkMessageSlots] = useMutation(
    DeleteAgendaAppointmentItemDocument,
    {
      refetchQueries: [GetBulkItemsDocument, GetBulkSlotsDocument],
    }
  );

  const onDelete = useCallback(
    async (item: BulkSlotItemDataFragment) => {
      try {
        const result = await removeBulkMessageSlots({
          variables: { id: item.appointment.id },
        });

        if (result.data?.appointmentDelete) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'bulk-messages.slot.remove.success',
            }),
          });
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    },
    [intl, removeBulkMessageSlots, showAlertMessage]
  );

  const onHandleSubmit = useCallback(() => {
    form.handleSubmit(async () => {
      const values = form.getValues();

      try {
        const result = await mutateAgendaAppointmentItem({
          variables: {
            ...values,
            id: null,
            healthProviderId,
            patientId: null,
            conversationId: null,
            conversationTitle: null,
            consultType: ConsultType.Bulk,
            startDateTime: range[0].startDateTime,
            endDateTime: range.at(-1)!.endDateTime,
            slots: Number(values.slots),
            bulkId: bulkItem?.id,
          },
        });

        if (result.data?.appointmentData.appointment) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'agenda.appointment-create.success',
            }),
          });

          resetSlots();

          // onSaved?.();

          // onClose?.();
        } else {
          throw new Error(
            'An unknown error has occurred in the server respond.'
          );
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    })();
  }, [
    bulkItem?.id,
    form,
    healthProviderId,
    intl,
    mutateAgendaAppointmentItem,
    range,
    resetSlots,
    showAlertMessage,
  ]);

  return (
    <>
      <Modal
        title={<CustomFormattedMessage id="bulk-messages.slots.title" />}
        width={ModalWidth.LARGE}
        onClose={onHandleCancel}
        buttons={
          <>
            {action === BulkMessageSlotModalAction.EDIT ? (
              <Button
                type={ButtonType.SECONDARY}
                inverted
                onClick={() => {
                  resetSlots();
                }}
              >
                <CustomFormattedMessage id="common.cancel" />
              </Button>
            ) : (
              <Button
                type={ButtonType.SECONDARY}
                inverted
                onClick={() => {
                  onHandleCancel?.();
                }}
              >
                <CustomFormattedMessage id="common.cancel" />
              </Button>
            )}

            {action === BulkMessageSlotModalAction.LIST ? (
              <Button
                disabled={!bulkItem?.active === true}
                icon={ButtonIcon.LIST_ADD}
                onClick={() => {
                  setAction(BulkMessageSlotModalAction.EDIT);
                }}
              >
                <CustomFormattedMessage id="bulk-messages.slots.add" />
              </Button>
            ) : (
              <Button
                icon={
                  mutateAgendaAppointmentItemLoading
                    ? ButtonIcon.LOADING
                    : ButtonIcon.NONE
                }
                disabled={
                  !formIsValid ||
                  range.length === 0 ||
                  mutateAgendaAppointmentItemLoading
                }
                onClick={onHandleSubmit}
              >
                <CustomFormattedMessage id="bulk-messages.slots.add" />
              </Button>
            )}
          </>
        }
      >
        {action === BulkMessageSlotModalAction.LIST ? (
          <div>
            <BulkMessagesSlotList
              items={bulkSlotItems}
              loading={bulkSlotItemsLoading}
              onDelete={onDelete}
            />
          </div>
        ) : (
          <FormProvider {...form}>
            <FormColumns>
              <FormRow>
                <InputFieldPager
                  onClickPrev={
                    prevDate ? () => onHandleDatePager('prev') : undefined
                  }
                  onClickNext={
                    nextDate ? () => onHandleDatePager('next') : undefined
                  }
                >
                  <InputField
                    type="date"
                    name="date"
                    label={
                      <>
                        <CustomFormattedMessage id="common.date" />
                        <small>*</small>
                      </>
                    }
                    icon={BaseInputIcon.DATE}
                    value={date}
                    min={formatDate(new Date())}
                    onChange={onHandleDate}
                  />
                </InputFieldPager>
              </FormRow>

              <FormRow>
                <InputSelectController<Honorific>
                  name="honorific"
                  label={
                    <>
                      <CustomFormattedMessage id="common.honorific" />
                      <small>*</small>
                    </>
                  }
                  placeholder={intl.formatMessage({
                    id: 'common.honorific-placeholder',
                  })}
                  options={honorifics}
                />
              </FormRow>
            </FormColumns>

            <h3>
              <CustomFormattedMessage id="agenda.appointment.slots" />
            </h3>

            {honorific && (
              <div className="honorific-block-times">
                <InputFieldController
                  name="slots"
                  min={1}
                  type="number"
                  defaultValue="1"
                  label={
                    <>
                      <CustomFormattedMessage id="common.maximum-slots" />
                      <small>*</small>
                    </>
                  }
                />
                <h4>
                  <CustomFormattedMessage id="common.minutes-select-placeholder" />
                </h4>
                <div className="honorific-block-times__content">
                  <InputCheckboxController<boolean>
                    name="irregular"
                    options={[
                      {
                        key: true,
                        title: intl.formatMessage({
                          id: 'agenda.appointment.irregular',
                        }),
                      },
                    ]}
                    value={irregular}
                  />

                  <InputSelectController<number>
                    name="minutes"
                    options={filteredBlockTimes}
                    placeholder={intl.formatMessage({
                      id: 'common.minutes-select-placeholder',
                    })}
                  />
                </div>
              </div>
            )}
            {irregular === true && (
              <Message
                type={MessageType.WARNING}
                message={
                  <CustomFormattedMessage id="common.errors.template-override" />
                }
              />
            )}

            {minutes ? (
              <>
                <AgendaAvailableBlockList
                  consultType={consultType}
                  showAsRange
                  loading={blocksLoading}
                  items={blocks}
                  date={date}
                  value={{
                    healthProfessionalId,
                    selectedSlots,
                  }}
                  onClick={onHandleSlots}
                />

                {next && (
                  <Button
                    className="first-available-date"
                    testId="first-available-date"
                    type={ButtonType.TEXT}
                    onClick={() => {
                      setDate(next);
                    }}
                  >
                    <CustomFormattedMessage
                      id="agenda.appointment.first-available-date"
                      values={{
                        date: (
                          <CustomFormattedDate value={next} weekday="long" />
                        ),
                      }}
                    />
                  </Button>
                )}
              </>
            ) : (
              <Message
                type={MessageType.INFO}
                message={
                  <CustomFormattedMessage id="agenda.appointment-create.no-minutes" />
                }
              />
            )}
          </FormProvider>
        )}
      </Modal>
      {cancelPromptOpen && (
        <ModalPrompt
          onConfirm={() => onHandleCancel(true)}
          onClose={() => setCancelPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage id="common.confirm-not-saved-changes" />
          </p>
        </ModalPrompt>
      )}
    </>
  );
}

export { BulkMessageSlotsModal };
